// src/components/ChatTab.jsx
import React, { useState, useEffect, useRef, useContext, useMemo } from "react";
import { FaUserCircle } from "react-icons/fa";
import { Menu } from "@headlessui/react";
import { SoundContext } from "../contexts/SoundContext";
import { ChatContext } from "../contexts/ChatContext";

const ChatTab = () => {
  const { isMuted } = useContext(SoundContext);
  const {
    socket,
    username,
    isLoggedIn,
    onlineUsers,
    loginError,
    selectedRecipient,
    isConnected,
    isAuthenticated,
    socketStatus,
    uploadNotices,
    login,
    logout,
    updateSelectedRecipient,
  } = useContext(ChatContext);

  const [messages, setMessages] = useState([]);
  // Global activity stays separate from replaceable private-chat history.
  const timeline = useMemo(() => [
    ...messages.map((message, index) => ({ ...message, kind: "message", key: `message-${index}` })),
    ...uploadNotices.map((notice) => ({ ...notice, kind: "upload", key: `upload-${notice.id}` })),
  ].sort((a, b) => (Date.parse(a.timestamp) || 0) - (Date.parse(b.timestamp) || 0)),
  [messages, uploadNotices]);
  const [inputMessage, setInputMessage] = useState("");
  const [usernameInput, setUsernameInput] = useState("");
  const [password, setPassword] = useState("");
  const [, setMessageStatus] = useState("");
  const messagesEndRef = useRef(null);
  const notificationAudioRef = useRef(null);
  const isMutedRef = useRef(isMuted);
  const hasRestoredRecipient = useRef(false);

  // Screen sharing states
  const [screenShareActive, setScreenShareActive] = useState(false);
  const [screenImage, setScreenImage] = useState(null);
  const [screenShareSender, setScreenShareSender] = useState(null);
  const [lastFrameTime, setLastFrameTime] = useState(null);
  const [timeAgo, setTimeAgo] = useState("just now");
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [isTheaterMode, setIsTheaterMode] = useState(false);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [isNativeFullscreen, setIsNativeFullscreen] = useState(false);
  const [panPosition, setPanPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const lightboxRef = useRef(null);

  // Keep the ref up to date with the isMuted state
  useEffect(() => {
    isMutedRef.current = isMuted;
  }, [isMuted]);

  // Set up message-related socket event listeners
  useEffect(() => {
    if (!socket) return;

    const handlePrivateMessage = (messageData) => {
      if (
        messageData.recipient === username ||
        messageData.sender === username
      ) {
        setMessages((prevMessages) => [...prevMessages, messageData]);

        // Play notification sound only when receiving a message (not sending) and not muted
        if (
          messageData.recipient === username &&
          messageData.sender !== username &&
          !isMutedRef.current
        ) {
          notificationAudioRef.current?.play().catch((error) => {
            console.error("Error playing notification sound:", error);
          });
        }
      }
    };

    const handleChatHistory = (data) => {
      setMessages(data.history);
    };

    const handleError = (data) => {
      setMessageStatus(`Error: ${data.message}`);
    };

    const handleScreenShareStatus = (data) => {
      if (data.action === "start") {
        setScreenShareActive(true);
        setScreenShareSender(data.sender);
        setIsMinimized(false);
      } else if (data.action === "stop") {
        setScreenShareActive(false);
        setScreenImage(null);
        setScreenShareSender(null);
        setIsLightboxOpen(false);
      }
    };

    const handleScreenShareFrame = (data) => {
      setScreenShareActive(true);
      setScreenShareSender(data.sender);
      setScreenImage(data.image);
      setLastFrameTime(Date.now());
    };

    socket.on("private_message", handlePrivateMessage);
    socket.on("private_chat_history", handleChatHistory);
    socket.on("error", handleError);
    socket.on("screen_share_status", handleScreenShareStatus);
    socket.on("screen_share_frame", handleScreenShareFrame);

    return () => {
      socket.off("private_message", handlePrivateMessage);
      socket.off("private_chat_history", handleChatHistory);
      socket.off("error", handleError);
      socket.off("screen_share_status", handleScreenShareStatus);
      socket.off("screen_share_frame", handleScreenShareFrame);
    };
  }, [socket, username]);

  // Update relative time and handle stream timeout fallback
  useEffect(() => {
    if (!screenShareActive) return;

    const intervalId = setInterval(() => {
      if (!lastFrameTime) {
        setTimeAgo("just now");
        return;
      }
      const diffSec = Math.floor((Date.now() - lastFrameTime) / 1000);
      if (diffSec < 2) {
        setTimeAgo("just now");
      } else if (diffSec < 60) {
        setTimeAgo(`${diffSec}s ago`);
      } else {
        setTimeAgo(`${Math.floor(diffSec / 60)}m ago`);
      }

      // If no new frame for 45s, assume stream stopped
      if (diffSec > 45) {
        setScreenShareActive(false);
        setScreenImage(null);
        setIsLightboxOpen(false);
      }
    }, 1000);

    return () => clearInterval(intervalId);
  }, [screenShareActive, lastFrameTime]);

  const handleOpenLightbox = () => {
    setZoomLevel(1);
    setPanPosition({ x: 0, y: 0 });
    setIsLightboxOpen(true);
  };

  const handleCloseLightbox = () => {
    if (document.fullscreenElement && document.exitFullscreen) {
      document.exitFullscreen().catch(() => {});
    }
    setIsLightboxOpen(false);
    setZoomLevel(1);
    setPanPosition({ x: 0, y: 0 });
  };

  const toggleNativeFullscreen = () => {
    if (!document.fullscreenElement) {
      const elem = lightboxRef.current || document.documentElement;
      if (elem.requestFullscreen) {
        elem.requestFullscreen().catch(() => {});
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen().catch(() => {});
      }
    }
  };

  // Sync native fullscreen state
  useEffect(() => {
    const onFullscreenChange = () => {
      setIsNativeFullscreen(Boolean(document.fullscreenElement));
    };
    document.addEventListener("fullscreenchange", onFullscreenChange);
    return () =>
      document.removeEventListener("fullscreenchange", onFullscreenChange);
  }, []);

  // Keyboard navigation inside lightbox
  useEffect(() => {
    if (!isLightboxOpen) return;
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        handleCloseLightbox();
      } else if (e.key === "f" || e.key === "F") {
        toggleNativeFullscreen();
      } else if (e.key === "+" || e.key === "=") {
        setZoomLevel((prev) => Math.min(3, +(prev + 0.25).toFixed(2)));
      } else if (e.key === "-" || e.key === "_") {
        setZoomLevel((prev) => {
          const next = Math.max(1, +(prev - 0.25).toFixed(2));
          if (next === 1) setPanPosition({ x: 0, y: 0 });
          return next;
        });
      } else if (e.key === "0") {
        setZoomLevel(1);
        setPanPosition({ x: 0, y: 0 });
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isLightboxOpen]);

  const handleWheel = (e) => {
    if (!isLightboxOpen) return;
    if (e.deltaY < 0) {
      setZoomLevel((prev) => Math.min(3, +(prev + 0.2).toFixed(2)));
    } else {
      setZoomLevel((prev) => {
        const next = Math.max(1, +(prev - 0.2).toFixed(2));
        if (next === 1) setPanPosition({ x: 0, y: 0 });
        return next;
      });
    }
  };

  const handleMouseDown = (e) => {
    if (zoomLevel <= 1) return;
    setIsDragging(true);
    setDragStart({
      x: e.clientX - panPosition.x,
      y: e.clientY - panPosition.y,
    });
  };

  const handleMouseMove = (e) => {
    if (!isDragging || zoomLevel <= 1) return;
    setPanPosition({
      x: e.clientX - dragStart.x,
      y: e.clientY - dragStart.y,
    });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    // Scroll to the bottom when messages change
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, uploadNotices]);

  // Handle login errors from context
  useEffect(() => {
    if (loginError) {
      setMessageStatus(`Login failed: ${loginError}`);
    }
  }, [loginError]);

  // Clear messages when logging in successfully
  useEffect(() => {
    if (isLoggedIn) {
      setMessageStatus("Logged in successfully.");
    }
  }, [isLoggedIn]);

  // Restore selected recipient when returning to chat tab
  useEffect(() => {
    if (
      isAuthenticated &&
      selectedRecipient &&
      socket &&
      !hasRestoredRecipient.current
    ) {
      // Check if the selected recipient is still online
      const isRecipientOnline = onlineUsers.some(
        (user) => user.value === selectedRecipient.value
      );

      if (isRecipientOnline) {
        // Fetch chat history for the selected recipient
        socket.emit("fetch_chat_history", {
          recipient: selectedRecipient.value,
        });
        hasRestoredRecipient.current = true;
      } else {
        // Recipient is no longer online, clear selection
        updateSelectedRecipient(null);
        hasRestoredRecipient.current = true;
      }
    }

    // Refetch history after logout or authentication of a replacement socket.
    if (!isAuthenticated) {
      hasRestoredRecipient.current = false;
    }
  }, [
    isAuthenticated,
    selectedRecipient,
    onlineUsers,
    socket,
    updateSelectedRecipient,
  ]);

  const handleLogin = () => {
    if (!usernameInput || !password) {
      setMessageStatus("Username and password are required.");
      return;
    }

    login(usernameInput, password);
    setPassword(""); // Clear password after login attempt
  };

  const handleLogout = () => {
    logout();
    setUsernameInput("");
    setPassword("");
    setMessages([]);
    setMessageStatus("Logged out.");
    setScreenShareActive(false);
    setScreenImage(null);
    setScreenShareSender(null);
    setIsLightboxOpen(false);
  };

  const handleSendMessage = () => {
    if (!isAuthenticated || !socket?.connected) {
      setMessageStatus("Chat is reconnecting. Please try again once connected.");
      return;
    }
    if (!selectedRecipient || !inputMessage.trim()) {
      setMessageStatus("Recipient and message are required.");
      return;
    }

    const messageData = {
      sender: username,
      recipient: selectedRecipient.value,
      message: inputMessage.trim(),
      timestamp: new Date().toISOString(),
    };

    socket.emit("private_message", messageData);
    setInputMessage("");
    setMessageStatus("Message sent.");
  };

  const handleRecipientChange = (selectedOption) => {
    updateSelectedRecipient(selectedOption);
    if (selectedOption && isAuthenticated && socket?.connected) {
      socket.emit("fetch_chat_history", { recipient: selectedOption.value });
    }
  };

  const isScreenActive = screenShareActive && screenImage && !isMinimized;

  return (
    <div
      className={`flex justify-center w-full min-h-[calc(100vh-4.5rem)] ${
        isScreenActive ? "pb-4 px-0 sm:px-2" : "p-2 sm:p-4"
      } bg-gray-100 dark:bg-gray-900 text-gray-900 dark:text-white transition-all duration-500 ease-in-out`}
    >
      {/* Chat Container */}
      <div
        className={`flex flex-col w-full h-[calc(100vh-5.5rem)] min-h-[580px] bg-white dark:bg-gray-800 shadow-xl rounded-xl overflow-hidden border border-gray-200/80 dark:border-gray-700 transition-[max-width,border-radius,box-shadow] duration-500 ease-in-out ${
          isScreenActive ? "max-w-full" : "max-w-3xl"
        }`}
      >
        {/* Header */}
        <header className="flex justify-between items-center p-4 bg-gray-200 dark:bg-gray-800">
          <div className="flex items-center space-x-3">
            <h2 className="text-2xl font-semibold">Chat</h2>
            <span
              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                isConnected
                  ? "bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300"
                  : socketStatus === "connecting"
                  ? "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300"
                  : "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300"
              }`}
            >
              <span
                className={`w-2 h-2 mr-1.5 rounded-full ${
                  isConnected
                    ? "bg-green-500 animate-pulse"
                    : socketStatus === "connecting"
                    ? "bg-yellow-500 animate-pulse"
                    : "bg-red-500"
                }`}
              />
              {isConnected
                ? "Live"
                : socketStatus === "connecting"
                ? "Connecting..."
                : "Disconnected"}
            </span>
          </div>
          {isLoggedIn && (
            <button
              onClick={handleLogout}
              className="px-3 py-1 bg-red-500 text-white rounded-md hover:bg-red-600 dark:bg-red-700 dark:hover:bg-red-800 focus:outline-none focus:ring-2 focus:ring-red-400"
            >
              Logout
            </button>
          )}
        </header>

        {!isLoggedIn ? (
          // Login Screen
          <div className="flex flex-1 items-center justify-center p-6 bg-gray-100 dark:bg-gray-700">
            <div className="w-full max-w-md p-6 bg-white dark:bg-gray-600 rounded-md shadow-md">
              <div className="space-y-4">
                {loginError && (
                  <div className="p-3 text-sm text-red-700 bg-red-100 border border-red-300 rounded-md dark:bg-red-900/50 dark:text-red-200 dark:border-red-700">
                    {loginError}
                  </div>
                )}
                <div>
                  <label className="block text-gray-700 dark:text-gray-300">
                    Username:
                  </label>
                  <input
                    type="text"
                    value={usernameInput}
                    onChange={(e) => setUsernameInput(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleLogin()}
                    className="mt-1 block w-full px-3 py-2 bg-gray-50 dark:bg-gray-600 border border-gray-300 dark:border-gray-500 rounded-md shadow-sm placeholder-gray-400 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-500 focus:border-blue-500 dark:focus:border-blue-500"
                    placeholder="Enter username"
                  />
                </div>

                <div>
                  <label className="block text-gray-700 dark:text-gray-300">
                    Password:
                  </label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && handleLogin()}
                    className="mt-1 block w-full px-3 py-2 bg-gray-50 dark:bg-gray-600 border border-gray-300 dark:border-gray-500 rounded-md shadow-sm placeholder-gray-400 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-500 focus:border-blue-500 dark:focus:border-blue-500"
                    placeholder="Enter password"
                  />
                </div>

                <button
                  onClick={handleLogin}
                  className="w-full px-4 py-2 bg-purple-500 text-white rounded-md hover:bg-purple-600 dark:bg-purple-600 dark:hover:bg-purple-700 focus:outline-none focus:ring-2 focus:ring-purple-400 transition-colors"
                >
                  Login
                </button>
              </div>
            </div>
          </div>
        ) : (
          // Authenticated Chat Interface
          <div className="flex flex-col flex-1 overflow-hidden">
            {/* Top Bar: Recipient Selector */}
            <div className="flex justify-between items-center p-4 bg-gray-200 dark:bg-gray-800 border-b border-gray-300 dark:border-gray-700">
              <Menu as="div" className="relative inline-block text-left w-full">
                <div>
                  <Menu.Button className="inline-flex justify-between w-full px-4 py-2 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm hover:bg-gray-200 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500">
                    {selectedRecipient
                      ? selectedRecipient.label
                      : "Select a user"}
                    <svg
                      className="w-5 h-5 ml-2 -mr-1"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                      aria-hidden="true"
                    >
                      <path
                        fillRule="evenodd"
                        d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </Menu.Button>
                </div>
                <Menu.Items className="absolute z-10 mt-2 w-full bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-white border border-gray-300 dark:border-gray-600 rounded-md shadow-lg">
                  {onlineUsers.length === 0 ? (
                    <div className="px-4 py-2 text-sm text-gray-500">
                      No users online
                    </div>
                  ) : (
                    onlineUsers.map((user) => (
                      <Menu.Item key={user.value}>
                        {({ active }) => (
                          <button
                            onClick={() => handleRecipientChange(user)}
                            className={`${
                              active
                                ? "bg-gray-200 dark:bg-gray-600 text-gray-900 dark:text-white"
                                : "text-gray-700 dark:text-gray-300"
                            } block w-full text-left px-4 py-2 text-sm hover:bg-gray-200 dark:hover:bg-gray-600 focus:outline-none`}
                          >
                            {user.label}
                          </button>
                        )}
                      </Menu.Item>
                    ))
                  )}
                </Menu.Items>
              </Menu>
            </div>

            {/* Unified Smooth Flex Layout (Chat + Live Screen) */}
            <div className="flex flex-1 flex-col lg:flex-row overflow-hidden w-full relative">
              {/* Left Column: Chat Messages and Input */}
              <div
                className={`flex flex-col border-b lg:border-b-0 lg:border-r border-gray-200 dark:border-gray-700 overflow-hidden bg-gray-50 dark:bg-gray-800/60 transition-all duration-500 ease-in-out ${
                  isScreenActive
                    ? isTheaterMode
                      ? "w-0 max-w-0 opacity-0 pointer-events-none p-0 border-0 flex-shrink-0"
                      : "w-full lg:w-80 xl:w-96 flex-shrink-0 opacity-100"
                    : "w-full flex-1 opacity-100"
                }`}
              >
                {/* Minimized Screen Share Banner if stream is active but minimized */}
                {screenShareActive && screenImage && isMinimized && (
                  <div className="flex justify-between items-center px-4 py-2 bg-emerald-950/60 border-b border-emerald-800/60 text-emerald-300 text-xs transition-all duration-300 flex-shrink-0">
                    <div className="flex items-center space-x-2">
                      <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />
                      <span>
                        Live screen from {screenShareSender || "user"} is streaming ({timeAgo})
                      </span>
                    </div>
                    <button
                      onClick={() => setIsMinimized(false)}
                      className="px-2.5 py-0.5 bg-emerald-600 hover:bg-emerald-500 text-white rounded text-xs transition-colors"
                    >
                      Show Screen
                    </button>
                  </div>
                )}

                {/* Messages Area */}
                <div className="flex-1 p-4 bg-gray-50 dark:bg-gray-700/60 overflow-y-auto min-h-0">
                  {timeline.length === 0 ? (
                    <div className="text-center text-gray-400">No messages yet.</div>
                  ) : (
                    timeline.map((msg) => msg.kind === "upload" ? (
                      <div key={msg.key} role="status" className="mb-4 mx-auto max-w-md rounded-md border border-gray-200 dark:border-gray-600 bg-gray-100 dark:bg-gray-800 px-3 py-2 text-sm text-gray-700 dark:text-gray-200">
                        <p className="font-medium">An image was uploaded</p>
                        <p className="break-all">Image: <span className="font-medium">{msg.filename}</span></p>
                        <p className="break-all">Topic: <span className="font-medium">{msg.topic}</span></p>
                        <time dateTime={msg.timestamp} className="mt-1 block text-xs text-gray-500 dark:text-gray-400">
                          {new Date(msg.timestamp).toLocaleString()}
                        </time>
                      </div>
                    ) : (
                      <div
                        key={msg.key}
                        className={`mb-4 flex ${
                          msg.sender === username ? "justify-end" : "justify-start"
                        }`}
                      >
                        <div
                          className={`flex items-start ${
                            msg.sender === username ? "flex-row-reverse" : ""
                          }`}
                        >
                          <FaUserCircle className="text-2xl mx-2 text-gray-500 dark:text-gray-300" />
                          <div
                            className={`max-w-xs md:max-w-md p-2 rounded-md shadow-md ${
                              msg.sender === username
                                ? "bg-blue-500 text-white hover:bg-blue-600 dark:bg-blue-600 dark:hover:bg-blue-700"
                                : "bg-gray-200 text-gray-800 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 dark:text-white"
                            } transition-colors`}
                          >
                            <strong>{msg.sender}:</strong> {msg.message}
                            <div
                              className={`text-xs ${
                                msg.sender === username
                                  ? "text-gray-100 dark:text-gray-300"
                                  : "text-gray-600 dark:text-gray-400"
                              } mt-1`}
                            >
                              {new Date(msg.timestamp).toLocaleString()}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                  <div ref={messagesEndRef} />
                </div>

                {/* Input Area */}
                <div className="p-3 bg-gray-200 dark:bg-gray-800 border-t border-gray-300 dark:border-gray-700 flex-shrink-0">
                  <div className="flex items-center">
                    <textarea
                      value={inputMessage}
                      onChange={(e) => setInputMessage(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && !e.shiftKey) {
                          e.preventDefault();
                          handleSendMessage();
                        }
                      }}
                      className="flex-1 px-3 py-2 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm placeholder-gray-400 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-500 focus:border-blue-500 dark:focus:border-blue-500 resize-none text-gray-900 dark:text-white text-sm"
                      placeholder="Type message (Enter to send)..."
                      rows={1}
                      onInput={(e) => {
                        e.target.style.height = "auto";
                        e.target.style.height = `${e.target.scrollHeight}px`;
                      }}
                    />
                    <button
                      onClick={handleSendMessage}
                      disabled={!isAuthenticated}
                      className="ml-2 px-3 py-2 bg-green-500 text-white text-sm rounded-md hover:bg-green-600 dark:bg-green-600 dark:hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-400 transition-colors disabled:opacity-50"
                    >
                      Send
                    </button>
                  </div>
                </div>
              </div>

              {/* Right Column: Live Screen Viewer (Smoothly slides and scales in/out) */}
              <div
                className={`flex flex-col bg-gray-950 text-white overflow-hidden transition-all duration-500 ease-in-out min-w-0 ${
                  isScreenActive
                    ? "flex-1 opacity-100 scale-100 p-3 sm:p-4 w-full"
                    : "w-0 max-w-0 flex-grow-0 opacity-0 scale-95 pointer-events-none p-0 border-0"
                }`}
              >
                <div className="flex justify-between items-center mb-3 pb-2 border-b border-gray-800 gap-2 flex-shrink-0">
                  <div className="flex items-center space-x-2.5 min-w-0">
                    <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse flex-shrink-0" />
                    <span className="font-semibold text-sm text-gray-100 truncate">
                      Live Screen ({screenShareSender || "Remote"})
                    </span>
                    <span className="text-xs text-gray-400 hidden sm:inline">
                      • Updated {timeAgo}
                    </span>
                  </div>

                  <div className="flex items-center space-x-1.5 sm:space-x-2 flex-shrink-0">
                    {/* Theater View Toggle */}
                    <button
                      onClick={() => setIsTheaterMode(!isTheaterMode)}
                      className={`px-2.5 py-1 text-xs rounded border transition-colors flex items-center space-x-1 ${
                        isTheaterMode
                          ? "bg-indigo-600 hover:bg-indigo-500 text-white border-indigo-500"
                          : "bg-gray-800 hover:bg-gray-700 text-gray-300 border-gray-700"
                      }`}
                      title={isTheaterMode ? "Show chat sidebar" : "Hide chat sidebar for full screen width"}
                    >
                      <span>{isTheaterMode ? "💬 Show Chat" : "🖥 Theater View"}</span>
                    </button>

                    {/* Fullscreen Button */}
                    <button
                      onClick={handleOpenLightbox}
                      className="px-2.5 py-1 text-xs bg-indigo-600/90 hover:bg-indigo-600 text-white rounded border border-indigo-500 transition-colors flex items-center space-x-1 font-medium shadow-sm"
                      title="Expand to true fullscreen focus mode"
                    >
                      <span>🔍 Fullscreen</span>
                    </button>

                    {/* Minimize Button */}
                    <button
                      onClick={() => setIsMinimized(true)}
                      className="px-2 py-1 text-xs bg-gray-800 hover:bg-gray-700 text-gray-400 hover:text-white rounded border border-gray-700 transition-colors"
                      title="Minimize screen"
                    >
                      Minimize
                    </button>
                  </div>
                </div>

                <div
                  className="flex-1 flex items-center justify-center bg-black/90 rounded-xl overflow-hidden cursor-pointer group relative min-h-0 h-full border border-gray-800 p-1 select-none"
                  onClick={handleOpenLightbox}
                  title="Click to zoom full screen"
                >
                  <img
                    src={screenImage || undefined}
                    alt="Remote Live Screen"
                    className="w-full h-full object-contain max-h-full transition-transform duration-200 group-hover:scale-[1.005]"
                  />
                  <div className="absolute inset-0 bg-black/35 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center pointer-events-none">
                    <span className="px-3.5 py-2 bg-black/85 text-white text-xs font-medium rounded-full backdrop-blur-md border border-white/20 shadow-xl">
                      🔍 Click for Fullscreen Focus Mode
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* True Fullscreen Modal for Focused View */}
      {isLightboxOpen && screenImage && (
        <div
          ref={lightboxRef}
          className="fixed inset-0 z-50 bg-black/98 flex flex-col items-center justify-center select-none overflow-hidden backdrop-blur-md"
          onWheel={handleWheel}
        >
          {/* Floating Top Controls Header - Doesn't eat into image vertical space */}
          <div className="absolute top-3 left-3 right-3 z-30 flex justify-between items-center pointer-events-none">
            {/* Live Status Pill */}
            <div className="pointer-events-auto flex items-center space-x-2.5 px-3.5 py-1.5 rounded-full bg-black/75 border border-white/10 backdrop-blur-md text-xs text-gray-200 shadow-2xl">
              <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 animate-pulse" />
              <span className="font-semibold">
                {screenShareSender || "Remote"}'s Live Screen
              </span>
              <span className="text-gray-400 hidden sm:inline">
                • Updated {timeAgo}
              </span>
            </div>

            {/* Controls Pill */}
            <div className="pointer-events-auto flex items-center space-x-1.5 sm:space-x-2 px-3 py-1.5 rounded-full bg-black/75 border border-white/10 backdrop-blur-md text-xs text-gray-200 shadow-2xl">
              {/* Zoom Out */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setZoomLevel((prev) => {
                    const next = Math.max(1, +(prev - 0.25).toFixed(2));
                    if (next === 1) setPanPosition({ x: 0, y: 0 });
                    return next;
                  });
                }}
                disabled={zoomLevel <= 1}
                className="w-7 h-7 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 disabled:opacity-30 disabled:hover:bg-white/10 text-white font-bold transition-colors"
                title="Zoom Out (-)"
              >
                −
              </button>

              {/* Zoom Indicator / Reset */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  if (zoomLevel === 1) {
                    setZoomLevel(1.5);
                  } else {
                    setZoomLevel(1);
                    setPanPosition({ x: 0, y: 0 });
                  }
                }}
                className="px-2.5 py-1 rounded-md bg-white/10 hover:bg-white/20 text-xs font-mono text-gray-200 hover:text-white transition-colors"
                title="Click to toggle Fit / 150%"
              >
                {zoomLevel === 1 ? "Fit" : `${Math.round(zoomLevel * 100)}%`}
              </button>

              {/* Zoom In */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setZoomLevel((prev) => Math.min(3, +(prev + 0.25).toFixed(2)));
                }}
                disabled={zoomLevel >= 3}
                className="w-7 h-7 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 disabled:opacity-30 disabled:hover:bg-white/10 text-white font-bold transition-colors"
                title="Zoom In (+)"
              >
                +
              </button>

              <div className="w-px h-4 bg-white/20 my-auto" />

              {/* Native Browser Fullscreen Toggle */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  toggleNativeFullscreen();
                }}
                className="px-2.5 py-1 rounded-md bg-white/10 hover:bg-white/20 text-xs text-gray-200 hover:text-white transition-colors flex items-center space-x-1"
                title="Toggle browser native fullscreen (F)"
              >
                <span>{isNativeFullscreen ? "⛶ Exit Full" : "⛶ Maximize"}</span>
              </button>

              {/* Close Button */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleCloseLightbox();
                }}
                className="px-2.5 py-1 bg-red-600/80 hover:bg-red-600 text-white rounded-md text-xs transition-colors font-medium flex items-center space-x-1"
                title="Close fullscreen (Esc)"
              >
                <span>✕ Close</span>
              </button>
            </div>
          </div>

          {/* Edge-to-Edge Image Viewport */}
          <div
            className={`w-full h-full flex items-center justify-center overflow-hidden relative ${
              zoomLevel > 1
                ? "cursor-grab active:cursor-grabbing"
                : "cursor-zoom-in"
            }`}
            onClick={(e) => {
              if (zoomLevel === 1) {
                setZoomLevel(1.5);
              } else if (!isDragging) {
                setZoomLevel(1);
                setPanPosition({ x: 0, y: 0 });
              }
            }}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
          >
            <img
              src={screenImage}
              alt="Remote Live Screen Fullscreen"
              style={
                zoomLevel > 1
                  ? {
                      transform: `translate(${panPosition.x}px, ${panPosition.y}px) scale(${zoomLevel})`,
                      transition: isDragging
                        ? "none"
                        : "transform 0.15s ease-out",
                    }
                  : undefined
              }
              className={`select-none pointer-events-auto ${
                zoomLevel === 1
                  ? "w-full h-full max-w-full max-h-full object-contain"
                  : "max-w-none max-h-none object-contain"
              }`}
            />
          </div>

          {/* Floating Bottom Hint */}
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 z-20 pointer-events-none opacity-60 hover:opacity-100 transition-opacity">
            <div className="px-3.5 py-1 rounded-full bg-black/70 border border-white/10 backdrop-blur-md text-[11px] text-gray-300 shadow-lg">
              Scroll wheel to zoom • Drag to pan • Press Esc or F for fullscreen
            </div>
          </div>
        </div>
      )}

      {/* Hidden audio element for notification sound */}
      <audio
        ref={notificationAudioRef}
        src="/notification.mp3"
        preload="auto"
        style={{ display: "none" }}
      />
    </div>
  );
};

export default ChatTab;
